import {
  Box,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import type { CoprocultivoFormValues, PorcentajeKey } from '../schemas';
import { CoprocultivoSumaChip } from './CoprocultivoSumaChip';

const GENEROS: { name: PorcentajeKey; label: string }[] = [
  { name: 'porcentaje_ostertagia', label: 'Ostertagia' },
  { name: 'porcentaje_trichostrongylus', label: 'Trichostrongylus' },
  { name: 'porcentaje_cooperia', label: 'Cooperia' },
  { name: 'porcentaje_haemonchus', label: 'Haemonchus' },
  { name: 'porcentaje_oesophagostomum', label: 'Oesophagostomum' },
  { name: 'porcentaje_nematodirus', label: 'Nematodirus' },
];

export function ResultadoParasitologicoSection(): JSX.Element {
  const { control } = useFormContext<CoprocultivoFormValues>();

  const [
    ostertagia,
    trichostrongylus,
    cooperia,
    haemonchus,
    oesophagostomum,
    nematodirus,
  ] = useWatch({
    control,
    name: [
      'porcentaje_ostertagia',
      'porcentaje_trichostrongylus',
      'porcentaje_cooperia',
      'porcentaje_haemonchus',
      'porcentaje_oesophagostomum',
      'porcentaje_nematodirus',
    ],
  });

  return (
    <>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={500}>
          Porcentajes por género
        </Typography>
        <CoprocultivoSumaChip
          ostertagia={ostertagia ?? null}
          trichostrongylus={trichostrongylus ?? null}
          cooperia={cooperia ?? null}
          haemonchus={haemonchus ?? null}
          oesophagostomum={oesophagostomum ?? null}
          nematodirus={nematodirus ?? null}
        />
      </Box>

      {/* Grid 2 columnas */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
          gap: 2,
          mb: 1.5,
        }}
      >
        {GENEROS.map(({ name, label }) => (
          <Controller
            key={name}
            control={control}
            name={name}
            render={({ field, fieldState }) => (
              <TextField
                size="small"
                fullWidth
                type="number"
                label={label}
                InputLabelProps={{ sx: { fontStyle: 'italic' } }}
                inputProps={{ min: 0, max: 100, step: 1 }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                value={field.value !== null && field.value !== undefined ? String(field.value) : ''}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '') {
                    field.onChange(null);
                    return;
                  }
                  const parsed = Number(v);
                  if (!isNaN(parsed)) field.onChange(parsed);
                }}
                onBlur={field.onBlur}
              />
            )}
          />
        ))}
      </Box>

      {/* Hint */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontStyle: 'italic', display: 'block', mb: 2 }}
      >
        La suma debe ser 100 (parasitismo identificado) o 0 (resultado negativo).
      </Typography>

      {/* Observación */}
      <Controller
        control={control}
        name="observacion"
        render={({ field }) => (
          <TextField
            size="small"
            fullWidth
            multiline
            rows={2}
            label="Observación"
            value={field.value ?? ''}
            onChange={(e) => field.onChange(e.target.value !== '' ? e.target.value : null)}
            onBlur={field.onBlur}
          />
        )}
      />
    </>
  );
}
